import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { StateLanguage } from '../../languages/config/StateLanguage';
import { ApiDigitalAuthInvitation } from '../../services/auth/invitation.service';
import { ApiDigitalAuthAuthentication } from '../../services/auth/authentication.service';
import { Error } from '../../models/error.model';
import { Message } from '../../models/message.model';
import { CompanyToken } from '../../models/company.model';
import { TokenSwitch } from '../../models/token.model';
import { GlobalSwal } from '../../styles/swal.style';
import { AppServiceToken } from '../../models/app.service.model';
import { Invitation } from '../../models/invitation.model';
import { modalHide } from '../../utils/modal.util';
import { getError, getMessage } from '../../languages/translations/response';
import { errorWebHandler } from '../../scripts/error.web.handler.script';
import { authApiKey, authRefresh, authSwitchCompany } from '../../scripts/authentication.script';
import { imageCompany } from '../../scripts/image.value.script';
import { datetimeFormat12h } from '../../scripts/date.time.format.script';
import { statusInvitationValue } from '../../libraries/status.invitation.library';
import { toastError } from '../../utils/toast.util';

import AppLoading from '../Loading';

export interface AppModalInvitationViewProps {
  appServiceForUser: AppServiceToken | null,
  companyForUser: CompanyToken | null,
  invitation: Invitation | null,
  loadInvitations: Function,
  reloadSession: Function
};

let errorResponse: Error, messageResponse: Message, tokenSwitchResponse: TokenSwitch;

const AppModalInvitationView: React.FunctionComponent<AppModalInvitationViewProps> = ({appServiceForUser, companyForUser, invitation, loadInvitations, reloadSession}) => {
  const {lang} = StateLanguage()
  const navigate = useNavigate()

  const [loadIndicator, setLoadIndicator] = useState({accept: 'off', reject: 'off'})

  const submitInvitationReply = async (action: string) => {
    if (!appServiceForUser || !invitation) return null

    if (action === 'accept') {
      setLoadIndicator({accept: 'on', reject: 'off'})

      await ApiDigitalAuthInvitation.postInvitationAccept(appServiceForUser.api_key, invitation.token).then( async (response) => {
        if (response.status === 200) {
          messageResponse = response.data
          const companyId = invitation.company_id?.id

          if (!companyId) {
            loadInvitations(appServiceForUser.api_key)
            executeHideModalInvitationCompany()
            GlobalSwal.fire({
              title: getMessage(messageResponse.message, lang.code),
              icon: 'success',
              showConfirmButton: false,
              timer: 2200
            }).then(() => {
              navigate('/main/dashboard', {replace: true})
            })
            return
          }

          await ApiDigitalAuthAuthentication.switchCompany(authApiKey(), authRefresh(), companyId).then( async (switchRes) => {
            if (switchRes.status === 200) {
              tokenSwitchResponse = switchRes.data
              authSwitchCompany(tokenSwitchResponse)
              executeHideModalInvitationCompany()

              await reloadSession(null, appServiceForUser.api_key, companyId, () => {
                GlobalSwal.fire({
                  title: getMessage(messageResponse.message, lang.code),
                  icon: 'success',
                  showConfirmButton: false,
                  timer: 2200
                }).then(() => {
                  navigate('/main/dashboard', {replace: true})
                })
              })
            } else {
              let errorData = switchRes.data
              toastError(getError(errorData?.code, lang.code, errorData?.message))
            }
          }).catch( () => {
            GlobalSwal.fire({
              title: lang.labels.exclamError,
              text: lang.labels.sorryLooksLikeThereAreSomeErrorstryAgain,
              icon: 'error',
              confirmButtonText: lang.labels.okGotIt
            })
          })
        } else {
          errorResponse = response.data

          GlobalSwal.fire({
            title: getError(errorResponse?.code, lang.code, errorResponse?.message),
            text: lang.labels.sorryLooksLikeThereAreSomeErrorstryAgain,
            icon: 'error',
            confirmButtonText: lang.labels.okGotIt
          })
        }
      }).catch( (error) => {
        errorWebHandler(lang, error)
      }).finally(() => {
        setLoadIndicator({accept: 'off', reject: 'off'})
      })
    } else {
      setLoadIndicator({accept: 'off', reject: 'on'})

      await ApiDigitalAuthInvitation.postInvitationReject(appServiceForUser.api_key, invitation.token).then( (response) => {
        if (response.status === 200) {
          messageResponse = response.data
          loadInvitations(appServiceForUser.api_key)

          GlobalSwal.fire({
            title: getMessage(messageResponse.message, lang.code),
            text: lang.labels.ifYouChangeYourMind,
            icon: 'success',
            confirmButtonText: lang.labels.okGotIt
          }).then( () => {
            executeHideModalInvitationCompany()
          })
        } else {
          errorResponse = response.data

          GlobalSwal.fire({
            title: getError(errorResponse?.code, lang.code, errorResponse?.message),
            text: lang.labels.sorryLooksLikeThereAreSomeErrorstryAgain,
            icon: 'error',
            confirmButtonText: lang.labels.okGotIt
          })
        }
      }).catch( (error) => {
        errorWebHandler(lang, error)
      }).finally(() => {
        setLoadIndicator({accept: 'off', reject: 'off'})
      })
    }
  }

  const executeHideModalInvitationCompany = () => {
    modalHide('modal-invitation-view')
  }

  return (
    <div id="modal-invitation-view" className="modal fade" tabIndex={-1} aria-hidden="true" data-bs-backdrop="static" data-bs-keyboard="false">
      <div className="modal-dialog modal-md">
        <div className="modal-content">
          { invitation
            ?
            <>
              <div className="modal-header pb-0 border-0 justify-content-end">
                <div className="btn btn-sm btn-icon btn-active-color-primary" onClick={executeHideModalInvitationCompany}>
                  <span className="svg-icon svg-icon-1">
                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                      <rect opacity="0.5" x="6" y="17.3137" width="16" height="2" rx="1" transform="rotate(-45 6 17.3137)" fill="black" />
                      <rect x="7.41422" y="6" width="16" height="2" rx="1" transform="rotate(45 7.41422 6)" fill="black" />
                    </svg>
                  </span>
                </div>
              </div>
              <div className="modal-body pt-0">
                <div className="mb-5">
                  <h2 className="text-center text-capitalize mb-3">{lang.labels.accessToCompany}</h2>
                  <div className="text-justify fs-6">
                    {lang.labels.youHaveAlreadyRespondedTo}
                    <strong className="mx-1">{invitation.invited_by?.first_name || ''} {invitation.invited_by?.last_name || ''}</strong>
                    {lang.labels.requestOnAccess}:
                    <strong className="text-lowercase ms-1">{statusInvitationValue(lang, invitation.status)}</strong>
                    .
                  </div>
                </div>
                <div className={`mb-3 ${invitation.status === "accept" ? "bg-light-success" : invitation.status === "reject" ? "bg-light-danger" : "bg-light"}`}>
                  <div className="d-flex flex-stack p-3">
                    <div className="d-flex align-items-center">
                      <div className="symbol symbol-50px bg-light">
                        <img src={imageCompany(invitation.company_id?.logo)} alt={lang.labels.logo} />
                      </div>
                      <div className="ms-5">
                        <div className="fs-6 fw-bold text-dark">{invitation.company_id?.name || ''}</div>
                        <div className="fw-semibold text-muted"></div>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="text-end fw-bolder fs-7">
                  {datetimeFormat12h(lang.code, invitation.created_at)}
                </div>
              </div>
              { invitation.status === "pending" &&
                <div className="modal-footer pt-0 border-0 justify-content-center align-items-end">
                  <div className="mx-3">
                    <button className="btn btn-danger" type="button" data-kt-indicator={loadIndicator.reject} onClick={() => submitInvitationReply("reject")}>
                      <span className="indicator-label">{lang.labels.reject}</span>
                      <span className="indicator-progress">
                        {lang.labels.pleaseWait}
                        <span className="spinner-border spinner-border-sm align-middle ms-2"></span>
                      </span>
                    </button>
                  </div>
                  <div className="mx-3">
                    <button className="btn btn-success" type="button" data-kt-indicator={loadIndicator.accept} onClick={() => submitInvitationReply('accept')}>
                      <span className="indicator-label">{lang.labels.accept}</span>
                      <span className="indicator-progress">
                        {lang.labels.pleaseWait}
                        <span className="spinner-border spinner-border-sm align-middle ms-2"></span>
                      </span>
                    </button>
                  </div>
                </div>
              }
            </>
            :
            <AppLoading></AppLoading>
          }
        </div>
      </div>
    </div>
  )
}

export default AppModalInvitationView;
